import { getP } from 'redux-polyglot'
import {
  INPUT_BILLING_ADDRESS,
  INPUT_BILLING_CITY,
  INPUT_BILLING_COUNTRY,
  INPUT_BILLING_STATE,
  INPUT_BILLING_ZIP,
  INPUT_CC_EXPIRATION_MONTH,
  INPUT_CC_EXPIRATION_YEAR,
  INPUT_CC_NUMBER,
  INPUT_CC_SECURITY_CODE,
  INPUT_EMAIL_ADDRESS,
  INPUT_FIRST_NAME,
  INPUT_LAST_NAME,
  INPUT_PHONE_NUMBER
} from '../constants/checkout'
import api from '../libs/api'
import { clearCartReference } from '../libs/helpers'
import { getCartReference } from '../selectors/cart'
import {
  getBillingAddress,
  getBillingCity,
  getBillingCountryCode,
  getBillingState,
  getBillingZip,
  getCCExpirationMonth,
  getCCExpirationYear,
  getCCNumber,
  getCCSecurityCode,
  getEmailAddress,
  getFirstName,
  getLastName,
  getPhoneNumber
} from '../selectors/checkout'

/**
 * Actions related to managing input fields
 */
export const FOCUS_INPUT = 'CHECKOUT/FOCUS_INPUT'
export const UPDATE_INPUT = 'CHECKOUT/UPDATE_INPUT'
export const DELETE_INPUT = 'CHECKOUT/DELETE_INPUT'
export const ERROR_INPUT = 'CHECKOUT/ERROR_INPUT'
export const SET_VALUE_INPUT = 'CHECKOUT/SET_VALUE_INPUT'

export const focusInput = (focusedInput, focusedRow, focusedCol) => {

  let newFocusedInput
  switch(focusedInput) {
    case INPUT_CC_NUMBER:
    case INPUT_CC_EXPIRATION_MONTH:
    case INPUT_CC_EXPIRATION_YEAR:
    case INPUT_CC_SECURITY_CODE:
    case INPUT_FIRST_NAME:
    case INPUT_LAST_NAME:
    case INPUT_EMAIL_ADDRESS:
    case INPUT_PHONE_NUMBER:
    case INPUT_BILLING_ADDRESS:
    case INPUT_BILLING_COUNTRY:
    case INPUT_BILLING_ZIP:
    case INPUT_BILLING_CITY:
    case INPUT_BILLING_STATE:
      newFocusedInput = focusedInput
      break;
    default:
      newFocusedInput = INPUT_CC_NUMBER
  }

  return {
    type: FOCUS_INPUT,
    payload: {
      focusedInput: newFocusedInput,
      focusedRow: focusedRow,
      focusedCol: focusedCol
    }
  }
}

export const updateInput = newKey => ({
  type: UPDATE_INPUT,
  payload: {
    newKey
  }
})

export const deleteInput = () => ({
  type: DELETE_INPUT
})

export const errorInput = (input, error) => ({
  type: ERROR_INPUT,
  payload: {
    input,
    error
  }
})

export const setValueInput = (input, value) => ({
  type: SET_VALUE_INPUT,
  payload: {
    input,
    value
  }
})

/**
 * Actions related to performing the payment
 */
export const CHECKOUT_REQUEST = 'CHECKOUT/CHECKOUT_REQUEST'
export const CHECKOUT_FAILURE = 'CHECKOUT/CHECKOUT_FAILURE'
export const PAY_NOW_REQUEST = 'CHECKOUT/PAY_NOW_REQUEST'
export const PAY_NOW_SUCCESS = 'CHECKOUT/PAY_NOW_SUCCESS'
export const PAY_NOW_FAILURE = 'CHECKOUT/PAY_NOW_FAILURE'

const checkoutRequest = () => ({
  type: CHECKOUT_REQUEST
})

const checkoutFailure = (error) => ({
  type: CHECKOUT_FAILURE,
  payload: error instanceof Error ? error : new Error(error),
  error: true
})

const payNowRequest = (orderId, gateway) => ({
  type: PAY_NOW_REQUEST,
  payload: {
    orderId,
    gateway
  }
})

const payNowSuccess = (reference, email) => {
  clearCartReference()
  return {
    type: PAY_NOW_SUCCESS,
    payload: {
      reference,
      email
    }
  }
}

const payNowFailure = (error) => ({
  type: PAY_NOW_FAILURE,
  payload: error instanceof Error ? error : new Error(error),
  error: true
})

const payNow = (orderId, gateway) => (dispatch, getState) => {
  dispatch(payNowRequest(orderId, gateway))

  const month = parseInt(getCCExpirationMonth(getState()), 10)
      let year = parseInt(getCCExpirationYear(getState()), 10)
      if (year < 100) {
        year += 2000
      }

  const email = getEmailAddress(getState())
  const orderData = {
    gateway_id: gateway.id,
    number: getCCNumber(getState()),
    month: month,
    year: year,
    code: getCCSecurityCode(getState()),
    first_name: getFirstName(getState()),
    last_name: getLastName(getState()),
    email: email,
    phone: getPhoneNumber(getState()),
    address: getBillingAddress(getState()),
    country: getBillingCountryCode(getState()),
    postcode: getBillingZip(getState()),
    city: getBillingCity(getState()),
    state: getBillingState(getState())
  }

  return api.payOrder(orderId, orderData)
    .then(res => res.json())
    .then(
      response => {
        if (response.status === 200) {
          if (response.data && response.data.status && response.data.status === 'paid') {
            if (response.data.merchant_invoice_id) {
              return dispatch(payNowSuccess(response.data.merchant_invoice_id, email))
            } else {
              return dispatch(payNowSuccess(response.data.reference, email))
            }
          } else {
            return dispatch(payNowFailure(getP(getState()).t("checkout_app.error_checkout_payment", { _: "Please check your payment information details and try again" })))
          }
        } else {
          return dispatch(payNowFailure(response.reason))
        }
      },
      () => dispatch(checkoutFailure(getP(getState()).t("checkout_app.error_processing_order", { _: "An error has occurred while processing the order" })))
    )
}

export const checkout = () => (dispatch, getState) => {
  dispatch(checkoutRequest())

  const cartReference = getCartReference(getState())
  const checkoutData = {
    email: getEmailAddress(getState()),
    phone: getPhoneNumber(getState()),
    first_name: getFirstName(getState()),
    last_name: getLastName(getState()),
    address: getBillingAddress(getState()),
    country: getBillingCountryCode(getState()),
    postcode: getBillingZip(getState()),
    city: getBillingCity(getState()),
    state: getBillingState(getState())
  }

  return api.checkout(cartReference, checkoutData)
    .then(res => res.json())
    .then(
      response => {
        if (response.status === 200) {
          if (response.data && response.data.payment_gateways && response.data.payment_gateways.length > 0) {
            let gateway = response.data.payment_gateways.find(gateway => gateway.slug === 'authorize')
            if (gateway === undefined) {
              gateway = response.data.payment_gateways[0]
            }
            return dispatch(payNow(response.data.id, response.data.payment_gateways[0]))
          } else {
            return dispatch(checkoutFailure(getP(getState()).t("checkout_app.error_retrieving_merchant_details", { _: "An error occurred while retrieving merchant details" })))
          }
        } else {
          return dispatch(checkoutFailure(response.reason))
        }
      },
      () => dispatch(checkoutFailure(getP(getState()).t("checkout_app.error_processing_order", { _: "An error has occurred while processing the order" })))
    )
}

/**
 * Actions related to setting status
 */
export const SET_STATUS = 'CHECKOUT/SET_STATUS'

export const setStatus = (status) => ({
  type: SET_STATUS,
  payload: {
    status
  }
})

/**
 * Actions related to focusing keyboard
 */
export const FOCUS_KEYBOARD = 'CHECKOUT/FOCUS_KEYBOARD'

export const focusKeyboard = (focus) => ({
  type: FOCUS_KEYBOARD,
  payload: {
    focus
  }
})

/**
 * Actions related to loading account details
 */
export const ACCOUNT_DETAILS_REQUEST = 'CHECKOUT/ACCOUNT_DETAILS_REQUEST'
export const ACCOUNT_DETAILS_SUCCESS = 'CHECKOUT/ACCOUNT_DETAILS_SUCCESS'
export const ACCOUNT_DETAILS_FAILURE = 'CHECKOUT/ACCOUNT_DETAILS_FAILURE'

const accountDetailsRequest = () => ({
  type: ACCOUNT_DETAILS_REQUEST
})

const accountDetailsSuccess = (accountDetails) => ({
  type: ACCOUNT_DETAILS_SUCCESS,
  payload: {
    accountDetails
  }
})

const accountDetailsFailure = (error) => ({
  type: ACCOUNT_DETAILS_FAILURE,
  payload: error instanceof Error ? error : new Error(error),
  error: true
})

export const getAccountDetails = () => dispatch => {
  dispatch(accountDetailsRequest())
  return api.getAccountDetails()
    .then(res => res.json())
    .then(
      response => {
        if (response.status === 200) {
          return dispatch(accountDetailsSuccess(response.me))
        } else {
          return dispatch(accountDetailsFailure(response.reason))
        }
      },
      () => dispatch(accountDetailsFailure(getP(getState()).t("checkout_app.error_loading_account_details", { _: "An error has occurred while loading account details" })))
    )
}


